import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { REQUEST_STATUS_COLORS, REQUEST_STATUS_LABELS, getMakerDisplayName } from "../constants";

const ApprovalDetailDialog = ({
  open,
  request,
  onClose,
  onApprove,
  onReject,
  onApply,
  isSubmitting,
}) => {
  const [comment, setComment] = useState("");

  React.useEffect(() => {
    if (open) setComment("");
  }, [open, request?.id]);

  if (!request) return null;

  const canApproveReject = request.status === "PENDING";
  const canApply = request.status === "APPROVED";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Approval request #{request.id}
        <Chip
          size="small"
          sx={{ ml: 1 }}
          label={REQUEST_STATUS_LABELS[request.status] ?? request.status}
          color={REQUEST_STATUS_COLORS[request.status] ?? "default"}
        />
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" gutterBottom>Summary</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>{request.summary || request.entity_label || "—"}</Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
          <Typography variant="body2"><strong>Entity:</strong> {request.entity_code}</Typography>
          <Typography variant="body2"><strong>Action:</strong> {request.action}</Typography>
          <Typography variant="body2"><strong>Amount:</strong> {request.amount ?? "—"}</Typography>
          <Typography variant="body2"><strong>Submitted by:</strong> {getMakerDisplayName(request)}</Typography>
          <Typography variant="body2">
            <strong>Submitted:</strong>{" "}
            {request.date_created ? moment(request.date_created).format("MMM D, YYYY HH:mm") : "—"}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Payload</Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: "grey.100",
            p: 2,
            borderRadius: 1,
            overflow: "auto",
            fontSize: 12,
            maxHeight: 240,
          }}
        >
          {JSON.stringify(request.payload ?? {}, null, 2)}
        </Box>

        {Array.isArray(request.decisions) && request.decisions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Decisions</Typography>
            {request.decisions.map((d) => (
              <Box key={d.id} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Step {d.step_order} — {d.decision} by {d.approver_role} (user #{d.approver_user_id})
                  {d.comment ? `: "${d.comment}"` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {d.date_created ? moment(d.date_created).format("MMM D, YYYY HH:mm") : ""}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {canApproveReject && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Comment (required for reject)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Close</Button>
        {canApply && (
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            onClick={() => onApply(request)}
          >
            {isSubmitting ? <CircularProgress size={20} /> : "Complete action"}
          </Button>
        )}
        {canApproveReject && (
          <>
            <Button
              color="error"
              disabled={isSubmitting || !comment.trim()}
              onClick={() => onReject(request.id, comment)}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={isSubmitting}
              onClick={() => onApprove(request.id, comment, request)}
            >
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalDetailDialog;
